/**
 * Display-only letter casing, derived from an assessment's identity.
 *
 * The letter-knowledge instrument a teacher selected (uppercase / lowercase /
 * sounds "skill") determines how its letters should LOOK on screen — a
 * "CORE B Lowercase Letter Names" assessment must show lowercase glyphs
 * everywhere it appears. Before v3.37.3 every results/groups surface hardcoded
 * `.toUpperCase()`, so lowercase skills showed uppercase glyphs and teachers
 * couldn't tell which skill they were looking at.
 *
 * This is the ONE place that transform lives. It is DISPLAY ONLY — stored
 * `responses` / `letterFluency` keys, document shapes, and score semantics are
 * never touched. Response lookups still try both cases at the data layer.
 *
 * Case rules (mirrors the score-entry convention in utils/letterOrders.js and
 * utils/incorrectResponses.js):
 *   - lowercase → lowercase glyph
 *   - uppercase → uppercase glyph
 *   - sounds    → the doc's `letterCase` when it carries one (v3.85.0 Sounds
 *                 variants: 'uppercase' | 'lowercase' | 'both'); absent →
 *                 uppercase glyph (the sounds test reuses the uppercase letter
 *                 shapes — see memory/project_assessment.md)
 *   - anything else (word-based tests, unknown/missing testType) → unchanged
 *
 * @param {object|string} assessment  the assessment object (reads `.testType`
 *                                     and `.letterCase`) or a bare testType string
 * @param {string} letter             the glyph to display
 * @returns {string} the letter cased for that assessment (or unchanged)
 */
import { glyphForCase, normalizeLetterCase } from './assessmentEntry';

export function formatLetterForAssessment(assessment, letter) {
  if (letter === null || letter === undefined) return letter;
  const testType =
    typeof assessment === 'string' ? assessment : assessment && assessment.testType;
  const s = String(letter);
  if (testType === 'lowercase') return s.toLowerCase();
  if (testType === 'sounds') return glyphForCase(s, letterCaseOf(assessment));
  if (testType === 'uppercase') return s.toUpperCase();
  return s;
}

/**
 * The case a Letter/Sound doc was given in: the doc's `letterCase` when it
 * carries a valid one, else uppercase (every doc before v3.85.0, and every
 * bare testType string). Vowel docs carry their own value too.
 */
export function letterCaseOf(assessment) {
  const value = assessment && typeof assessment === 'object' ? assessment.letterCase : undefined;
  return normalizeLetterCase(value);
}

/** True when the doc carries a `letterCase` field at all (the tag renders only then). */
export function hasLetterCase(assessment) {
  return !!assessment && typeof assessment === 'object' && typeof assessment.letterCase === 'string'
    && normalizeLetterCase(assessment.letterCase) === assessment.letterCase;
}

/**
 * Resolve the display case for an aggregate letter view whose selector can be
 * a cross-case "all" as well as a single skill — the Small Groups focus
 * (`all` | `uppercase` | `lowercase` | `sounds`) and the Results detailed-grid
 * test-type filter (`All` | `uppercase` | `lowercase` | `sounds`).
 *
 * In these views the letters are stored/aggregated case-insensitively, so only
 * an explicit LOWERCASE selection should render lowercase; the cross-case
 * "all"/"All" aggregate (and anything else) shows canonical uppercase — the
 * letter's name. Feed the result to formatLetterForAssessment().
 *
 * @param {string} selection  the focus/filter value
 * @returns {'uppercase'|'lowercase'|'sounds'} a testType for formatLetterForAssessment
 */
export function letterCaseForSelection(selection) {
  return selection === 'lowercase' || selection === 'uppercase' || selection === 'sounds'
    ? selection
    : 'uppercase';
}
